import { Box, Card, Text } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, RowClassParams, RowClickedEvent } from "ag-grid-enterprise";
import { FaCheck, FaExclamation } from "react-icons/fa";
import { setSelectedSpaceValidationInRedux } from "../../DesignTab/designSlice";
import { ISpaceValidation, SPACE_VALIDATION_ID_ENUM } from "./spaceValidationsUtils";
import { compareDevicesInStandardroom } from "./StandardRoomCompare/standardRoomCompareUtils";
import { StandardRoomCompareTable } from "./StandardRoomCompare/StandardRoomCompareTable";
import { IMcBimItem } from "@backend/src/shared/schemas/schemas";
import { IDevice } from "@multiconsult-group/multiplacer-core";
import SprinklerSquareWallRuleViewer from "./SprinklerSquareWallRuleViewer";
import { selectHttpCurrentProjct } from "../../projectSelectors";

const RoomCeilingValidations: React.FC<{
  spaceValidations: ISpaceValidation[];
  devices: IDevice[];
  mcbimItemsInStandardSpace: IMcBimItem[];
}> = ({ spaceValidations, devices, mcbimItemsInStandardSpace }) => {
  const dispatch = useAppDispatch();

  const selectedSpaceValidationId = useAppSelector((state) => state.design.selectedSpaceValidation);

  const project = useAppSelector(selectHttpCurrentProjct);

  const clickedValidation = spaceValidations.find(
    (validation) => validation.id === selectedSpaceValidationId
  );

  const columns: ColDef<ISpaceValidation>[] = [
    { field: "id", hide: true },
    {
      field: "status",
      headerName: "",
      width: 50,
      cellRenderer: (params: ICellRendererParams<ISpaceValidation>) => {
        if (params.value === "ERROR" || params.value === "WARNING") {
          return <FaExclamation color="red" />;
        }
        if (params.value === "VALID") {
          return <FaCheck color="green" />;
        }
      },
    },
    { field: "title", headerName: "Title", flex: 1 },
  ];

  const rowClassRules = {
    "ag-row-selected": (params: RowClassParams<ISpaceValidation>) =>
      params.data?.id === selectedSpaceValidationId,
  };

  //const compareItems = compareDevicesInStandardroom(devices, mcbimItemsInStandardSpace);
  const compareItems = compareDevicesInStandardroom({
    devices: devices,
    mcbimItemsInStandardSpace: mcbimItemsInStandardSpace,
    compCodePath: project.compCodePath as string,
  });

  return (
    <Card h="100%">
      <Box h="40%" w="100%" className="ag-theme-quartz" style={{ overflow: "auto" }}>
        <AgGridReact
          getRowId={(p) => p.data.id}
          columnDefs={columns}
          rowData={spaceValidations}
          onRowClicked={(event: RowClickedEvent<ISpaceValidation>) => {
            dispatch(
              setSelectedSpaceValidationInRedux(
                event.data?.id as unknown as SPACE_VALIDATION_ID_ENUM
              )
            );
          }}
          rowClassRules={rowClassRules}
        />
      </Box>
      <Box
        h="60%"
        w="100%"
        pt="sm"
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflowY: "auto",
          gap: 8,
        }}
      >
        {clickedValidation != null && (
          <>
            <Text size="sm" c="dimmed" lh={1.5}>
              {clickedValidation.description}
            </Text>
            {clickedValidation.errorText && (
              <Text size="sm" c="red.7">
                {clickedValidation.errorText}
              </Text>
            )}
            {clickedValidation.id === SPACE_VALIDATION_ID_ENUM.STANDARD_ROOM && (
              <StandardRoomCompareTable
                compareItems={compareItems}
                devices={devices}
                mcbimItemsInStandardSpace={mcbimItemsInStandardSpace}
              />
            )}
            <Box w="100%" style={{ flex: 1, minHeight: 0 }}>
              {clickedValidation.id ===
                SPACE_VALIDATION_ID_ENUM.ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE && (
                <SprinklerSquareWallRuleViewer />
              )}
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

export default RoomCeilingValidations;
